import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { ProntuarioData } from '../../pages/admin/ProntuarioPage';

interface InvalidateEvolutionModalProps {
  evolution: ProntuarioData;
  onClose: () => void;
  onConfirm: (evolutionId: string, justification: string) => Promise<boolean>;
}


const InvalidateEvolutionModal: React.FC<InvalidateEvolutionModalProps> = ({
  evolution,
  onClose,
  onConfirm
}) => {
  const [justification, setJustification] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const extractTextFromHTML = (htmlString: string): string => {
    if (!htmlString || typeof htmlString !== 'string') {
      return 'Não informado';
    }
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlString;
      return tempDiv.textContent || tempDiv.innerText || 'Não informado';
    } catch (error) {
      return 'Não informado';
    }
  };

  const formatDate = (dateValue: any): string => {
    if (!dateValue) return 'N/A';
    try {
      let date: Date;
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else {
        return 'N/A';
      }
      return date.toLocaleString('pt-BR');
    } catch (error) {
      return 'N/A';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!justification.trim()) {
      toast.error('A justificativa é obrigatória');
      return;
    }

    if (justification.trim().length < 10) {
      toast.error('A justificativa deve ter pelo menos 10 caracteres');
      return;
    }

    if (!confirmChecked) {
      toast.error('Você deve confirmar a ação antes de prosseguir');
      return;
    }

    try {
      setIsSubmitting(true);
      const wasCompleted = await onConfirm(evolution.id, justification.trim());
      if (wasCompleted) {
        onClose();
      }
    } catch (error) {
      console.error('Erro ao invalidar evolução:', error);
      toast.error('Erro ao invalidar evolução');
    } finally {
      setIsSubmitting(false);
    }
  };

  const patientName = evolution.patientFullName || 'Paciente não identificado';
  const evolutionText = extractTextFromHTML(evolution.recordDescription || '');
  const evolutionPreview = evolutionText.length > 200 ? evolutionText.substring(0, 200) + '...' : evolutionText;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center">
              <div className="bg-red-100 p-2 rounded-full mr-3">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Invalidar Registro de Evolução</h2>
                <p className="text-sm text-gray-600">Esta ação não pode ser desfeita</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isSubmitting}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Atenção</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Ao invalidar este registro de evolução, ele será marcado como invalidado e não aparecerá nas
                    listagens padrão. No entanto, todos os dados serão preservados no sistema para
                    fins de auditoria e histórico.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Resumo da Evolução</h3>
            <div className="space-y-2 text-sm">
              <div className="flex">
                <span className="font-medium text-gray-600 w-32">Paciente:</span>
                <span className="text-gray-900">{patientName}</span>
              </div>
              <div className="flex">
                <span className="font-medium text-gray-600 w-32">Prontuário:</span>
                <span className="text-gray-900">{evolution.medicalRecordNumber || 'N/A'}</span>
              </div>
              <div className="flex">
                <span className="font-medium text-gray-600 w-32">Data:</span>
                <span className="text-gray-900">{formatDate(evolution.recordDate)}</span>
              </div>
              <div className="mt-3">
                <span className="font-medium text-gray-600">Evolução:</span>
                <div className="mt-1 p-3 bg-white border border-gray-200 rounded text-gray-700 text-xs max-h-24 overflow-y-auto">
                  {evolutionPreview}
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Justificativa para Invalidação *
                <span className="text-xs text-gray-500 ml-2">(mínimo 10 caracteres)</span>
              </label>
              <textarea
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={4}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500"
                placeholder="Descreva o motivo da invalidação deste registro de evolução..."
                required
                disabled={isSubmitting}
                minLength={10}
              />
              <p className="mt-1 text-xs text-gray-500">
                Caracteres: {justification.length}
              </p>
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="confirm-invalidation"
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="confirm-invalidation" className="font-medium text-gray-700">
                  Confirmo que desejo invalidar este registro de evolução
                </label>
                <p className="text-gray-500">
                  Entendo que esta ação será registrada no histórico e incluirá meus dados de usuário.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !confirmChecked || justification.trim().length < 10}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Invalidando...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4" />
                    Confirmar Invalidação
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvalidateEvolutionModal;
